import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/axios';
import { connectSocket } from '@/lib/socket';

export interface ChatMessage {
  id: string;
  senderId: string | null;
  type: string;
  content: string;
  fileUrl: string | null;
  fileName: string | null;
  reactions: Record<string, string[]>;
  readBy: string[];
  createdAt: string;
}

/** Socket-backed meeting chat: history + live messages, typing, reactions, files. */
export function useMeetingChat(meetingId: string, participantId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [typingPeers, setTypingPeers] = useState<string[]>([]);
  const socketRef = useRef<ReturnType<typeof connectSocket> | null>(null);

  useEffect(() => {
    const socket = connectSocket();
    socketRef.current = socket;

    if (participantId) socket.emit('meeting:subscribe', { meetingId, participantId });

    const onMessage = (m: ChatMessage) => setMessages((prev) => [...prev, m]);
    const onReaction = (m: ChatMessage) => setMessages((prev) => prev.map((x) => (x.id === m.id ? m : x)));
    const onTyping = ({ participantId: pid, isTyping }: { participantId: string; isTyping: boolean }) =>
      setTypingPeers((prev) => (isTyping ? [...new Set([...prev, pid])] : prev.filter((p) => p !== pid)));

    socket.on('chat:message', onMessage);
    socket.on('chat:reaction', onReaction);
    socket.on('chat:typing', onTyping);

    // load history
    api
      .get(`/chat/${meetingId}/history`)
      .then(({ data }) => setMessages(data.data.messages))
      .catch(() => undefined);

    return () => {
      socket.off('chat:message', onMessage);
      socket.off('chat:reaction', onReaction);
      socket.off('chat:typing', onTyping);
    };
  }, [meetingId, participantId]);

  const send = useCallback(
    (content: string) => {
      socketRef.current?.emit('chat:send', { meetingId, content });
    },
    [meetingId],
  );

  const react = useCallback((messageId: string, emoji: string) => {
    socketRef.current?.emit('chat:react', { messageId, emoji });
  }, []);

  const setTyping = useCallback(
    (isTyping: boolean) => {
      socketRef.current?.emit('chat:typing', { meetingId, isTyping });
    },
    [meetingId],
  );

  const uploadFile = useCallback(
    async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/chat/${meetingId}/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    [meetingId],
  );

  return { messages, typingPeers, send, react, setTyping, uploadFile };
}
